/**
 * API Service for Lions Magazine Archive
 * Handles all HTTP requests to the Django backend
 */
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://lionmagazine-backend-139572186833.asia-southeast1.run.app/api';
const AUTH_BASE_URL = import.meta.env.VITE_AUTH_URL || 'https://d303hub-dev-139572186833.asia-southeast1.run.app/api';
// Same-origin proxy: Vite (dev) and nginx (prod) inject X-Lions-Shared-Secret.
// Do not call Cloud Run directly from the browser — VITE_* env on Cloud Run
// is not available to the built JS unless set at `npm run build` time.
const CHATBOT_BASE_URL = '/chatbot-api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — DRF Token auth
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Tag-management GET endpoints require auth — do not strip the token on 401 retry.
const AUTH_REQUIRED_GET_PATHS = [
  '/service-types/',
  '/beneficiaries/',
  '/beneficiary-organizations/',
  '/co-organizers/',
  '/keywords/',
];

function getRequiresAuth(request) {
  const url = request?.url || '';
  return AUTH_REQUIRED_GET_PATHS.some((path) => url.includes(path));
}

// Response interceptor — clear token and redirect on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // For GET requests, retry once without the auth header so that
      // IsAuthenticatedOrReadOnly endpoints (e.g. /pages/search/) succeed
      // as anonymous requests rather than triggering a hard logout redirect.
      if (
        originalRequest &&
        originalRequest.method === 'get' &&
        !originalRequest._retried &&
        !getRequiresAuth(originalRequest)
      ) {
        originalRequest._retried = true;
        delete originalRequest.headers['Authorization'];
        return apiClient(originalRequest);
      }

      // For write requests (POST / PUT / PATCH / DELETE), clear the invalid
      // token and redirect to the membership portal — unless the caller opted out
      // (e.g. best-effort background syncs that should never boot the user out on failure).
      if (originalRequest && originalRequest.method !== 'get' && !originalRequest._skipLogout) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/auth/callback' && currentPath !== '/auth/share-link') {
          window.location.replace('https://portal.lionsclubs.org.hk');
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password, localLogin = false) => {
    // Use plain axios so the 401 interceptor doesn't redirect during login
    const url = localLogin
      ? `${API_BASE_URL}/auth/login/`
      : `${AUTH_BASE_URL}/auth/login/`;
    const response = await axios.post(url, { username, password });
    const { token, ...userInfo } = response.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(userInfo));
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    window.location.replace('https://portal.lionsclubs.org.hk');
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;
    // If the token is a JWT, decode and check the exp claim
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp && Date.now() / 1000 > payload.exp) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          return false;
        }
      }
    } catch {
      // Not a JWT or malformed — fall through and treat as valid
    }
    return true;
  },

  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem('auth_user') || 'null');
    } catch {
      return null;
    }
  },

  fetchMe: async () => {
    const response = await apiClient.get('/auth/me/');
    localStorage.setItem('auth_user', JSON.stringify(response.data));
    return response.data;
  },

  isAdmin: () => {
    try {
      const user = JSON.parse(localStorage.getItem('auth_user') || 'null');
      return !!(user?.is_staff || user?.is_superuser);
    } catch {
      return false;
    }
  },
};

// Shareable login links (admin only)
export const shareLinkAPI = {
  list: () => apiClient.get('/auth/share-links/'),
  create: (data) => apiClient.post('/auth/share-links/', data),
  revoke: (id) => apiClient.post(`/auth/share-links/${id}/revoke/`),
};

// Issues API
export const issuesAPI = {
  getAll: (params) => apiClient.get('/issues/', { params }),
  
  getById: (id) => apiClient.get(`/issues/${id}/`),
  
  getFeatured: () => apiClient.get('/issues/featured/'),
  
  getYears: () => apiClient.get('/issues/years/'),
  
  checkExists: (year, quarter) => apiClient.get('/issues/', { 
    params: { year, quarter } 
  }),

  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return apiClient.post('/issues/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  getUploadUrl: (filename, contentType, fileType) => {
    return apiClient.post('/issues/get_upload_url/', {
      filename,
      content_type: contentType,
      file_type: fileType
    });
  },
  
  uploadToGcs: (url, file, contentType) => {
    return axios.put(url, file, {
      headers: {
        'Content-Type': contentType || file.type,
      },
    });
  },
  
  update: (id, data) => apiClient.patch(`/issues/${id}/`, data),
  
  delete: (id) => apiClient.delete(`/issues/${id}/`),
  
  download: (id) => apiClient.get(`/issues/${id}/download/`),
  
  reprocess: (id) => apiClient.post(`/issues/${id}/reprocess/`),

  exportCsvToSupabase: () => apiClient.post('/issues/export_csv_to_webhook/'),
};

// Pages API
export const pagesAPI = {
  getAll: (params) => apiClient.get('/pages/', { params }),
  
  getById: (id) => apiClient.get(`/pages/${id}/`),
  
  getByIssueAndNumber: (issueId, pageNumber) => apiClient.get('/pages/', { 
    params: { issue: issueId, page_number: pageNumber } 
  }),
  
  search: (params) => apiClient.get('/pages/search/', { params }),
  
  getClubs: (q = '', page = 1, pageSize = 200) => apiClient.get('/pages/clubs/', { params: { q, page, page_size: pageSize } }),
  
  getServiceTypes: (q = '', page = 1, pageSize = 50) => apiClient.get('/pages/service_types/', { params: { q, page, page_size: pageSize } }),
  
  getBeneficiaries: (q) => apiClient.get('/pages/beneficiaries/', { params: { q } }),
  
  getPartners: (q = '', page = 1, pageSize = 50) => apiClient.get('/pages/partners/', { params: { q, page, page_size: pageSize } }),
  
  update: (id, data) => apiClient.patch(`/pages/${id}/`, data),

  download: (id) => apiClient.get(`/pages/${id}/download/`),

  autocomplete: (field, query) => apiClient.get('/pages/search/', {
    params: { [`${field}__icontains`]: query },
  }),
};

// Photos API
export const photosAPI = {
  getAll: (params) => apiClient.get('/photos/', { params }),
  
  getById: (id) => apiClient.get(`/photos/${id}/`),
  
  search: (params) => apiClient.get('/photos/search/', { params }),
  
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return apiClient.post('/photos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  update: (id, data) => apiClient.patch(`/photos/${id}/`, data),

  delete: (id) => apiClient.delete(`/photos/${id}/`),

  uploadForPage: (pageId, imageFile) => {
    const formData = new FormData();
    formData.append('page', pageId);
    formData.append('image', imageFile);
    return apiClient.post('/photos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  download: (id) => apiClient.get(`/photos/${id}/download/`),
};

// 獅聲心語 (Lion Voice) API
export const lionVoiceAPI = {
  getAll: async (params) => {
    const response = await apiClient.get('/lionvoice/episodes/', {
      params: { page_size: 100, ...params },
    });
    const data = response.data;

    if (Array.isArray(data)) {
      return { ...response, data };
    }
    if (!data.next) {
      return response;
    }

    const results = [...(data.results || [])];
    let nextUrl = data.next;
    while (nextUrl) {
      const pageResponse = await apiClient.get(nextUrl);
      const pageData = pageResponse.data;
      results.push(...(pageData.results || []));
      nextUrl = pageData.next;
    }
    return {
      ...response,
      data: { ...data, results, count: results.length, next: null, previous: null },
    };
  },
  getById: (id) => apiClient.get(`/lionvoice/episodes/${id}/`),
};

// Tags API
export const tagsAPI = {
  getAll: (params) => apiClient.get('/tags/', { params }),
  
  getById: (id) => apiClient.get(`/tags/${id}/`),
  
  getCategories: () => apiClient.get('/tags/categories/'),
};

// Tag Management API (ServiceTypes, Beneficiaries, BeneficiaryOrganizations, CoOrganizers, Keywords)
export const tagManagementAPI = {
  // Service types
  getServiceTypes: (params) => apiClient.get('/service-types/', { params }),
  createServiceType: (data) => apiClient.post('/service-types/', data),
  updateServiceType: (id, data) => apiClient.patch(`/service-types/${id}/`, data),
  deleteServiceType: (id) => apiClient.delete(`/service-types/${id}/`),
  mergeServiceTypes: (canonicalId, ids) =>
    apiClient.post('/service-types/merge/', { canonical_id: canonicalId, ids }),
  scanServiceType: (id, threshold = 0.55) =>
    apiClient.post(`/service-types/${id}/scan_pages/`, { threshold }),
  getServiceTypeScanStatus: (id) =>
    apiClient.get(`/service-types/${id}/scan_status/`),

  // Beneficiaries
  getBeneficiaries: (params) => apiClient.get('/beneficiaries/', { params }),
  createBeneficiary: (data) => apiClient.post('/beneficiaries/', data),
  updateBeneficiary: (id, data) => apiClient.patch(`/beneficiaries/${id}/`, data),
  deleteBeneficiary: (id) => apiClient.delete(`/beneficiaries/${id}/`),
  mergeBeneficiaries: (canonicalId, ids) =>
    apiClient.post('/beneficiaries/merge/', { canonical_id: canonicalId, ids }),
  scanBeneficiary: (id, threshold = 0.55) =>
    apiClient.post(`/beneficiaries/${id}/scan_pages/`, { threshold }),
  getBeneficiaryScanStatus: (id) =>
    apiClient.get(`/beneficiaries/${id}/scan_status/`),

  // Beneficiary Organizations
  getBeneficiaryOrgs: (params) => apiClient.get('/beneficiary-organizations/', { params }),
  createBeneficiaryOrg: (data) => apiClient.post('/beneficiary-organizations/', data),
  updateBeneficiaryOrg: (id, data) => apiClient.patch(`/beneficiary-organizations/${id}/`, data),
  deleteBeneficiaryOrg: (id) => apiClient.delete(`/beneficiary-organizations/${id}/`),
  mergeBeneficiaryOrgs: (canonicalId, ids) =>
    apiClient.post('/beneficiary-organizations/merge/', { canonical_id: canonicalId, ids }),

  // Co-organizers
  getCoOrganizers: (params) => apiClient.get('/co-organizers/', { params }),
  createCoOrganizer: (data) => apiClient.post('/co-organizers/', data),
  updateCoOrganizer: (id, data) => apiClient.patch(`/co-organizers/${id}/`, data),
  deleteCoOrganizer: (id) => apiClient.delete(`/co-organizers/${id}/`),
  mergeCoOrganizers: (canonicalId, ids) =>
    apiClient.post('/co-organizers/merge/', { canonical_id: canonicalId, ids }),
  scanCoOrganizer: (id, threshold = 0.55) =>
    apiClient.post(`/co-organizers/${id}/scan_pages/`, { threshold }),
  getCoOrganizerScanStatus: (id) =>
    apiClient.get(`/co-organizers/${id}/scan_status/`),

  // Keywords
  getKeywords: (params) => apiClient.get('/keywords/', { params }),
  createKeyword: (data) => apiClient.post('/keywords/', data),
  updateKeyword: (id, data) => apiClient.patch(`/keywords/${id}/`, data),
  deleteKeyword: (id) => apiClient.delete(`/keywords/${id}/`),
  mergeKeywords: (canonicalId, ids) =>
    apiClient.post('/keywords/merge/', { canonical_id: canonicalId, ids }),
  scanKeyword: (id, threshold = 0.55) =>
    apiClient.post(`/keywords/${id}/scan_pages/`, { threshold }),
  getKeywordScanStatus: (id) =>
    apiClient.get(`/keywords/${id}/scan_status/`),

  // Pages tagged with a specific tag
  getTagPages: (tagType, tagId, params) =>
    apiClient.get(`/${tagType}/${tagId}/pages/`, { params }),
};

// Search API
export const searchAPI = {
  // Use plain axios (no auth interceptor) so a 401 on /search-logs/ never
  // triggers the global logout redirect.
  log: (query, language, resultsCount) =>
    axios
      .post(`${API_BASE_URL}/search-logs/`, { query, language, results_count: resultsCount })
      .catch(() => {}), // silently ignore failures

  getPopular: () => apiClient.get('/search-logs/popular/'),
};

// AI Chat API
export const aiAPI = {
  chat: (message, sessionId, language = 'zh') =>
    apiClient.post('/ai/chat/', {
      message,
      sessionId: sessionId,
      language,
    }),
};

function parseSSEChunk(buffer) {
  const events = [];
  const parts = buffer.split('\n\n');
  const remainder = parts.pop() ?? '';
  for (const part of parts) {
    if (!part.trim()) continue;
    let event = 'message';
    let data = '';
    for (const line of part.split('\n')) {
      const trimmed = line.replace(/\r$/, '');
      if (trimmed.startsWith(':')) continue;
      if (trimmed.startsWith('event: ')) event = trimmed.slice(7).trim();
      else if (trimmed.startsWith('data: ')) data += trimmed.slice(6);
    }
    if (!data) continue;
    try {
      events.push({ event, data: JSON.parse(data) });
    } catch {
      events.push({ event, data });
    }
  }
  return { events, remainder };
}

/** Stream chat from chatbot-api (POST /chat, SSE). */
export const chatbotAPI = {
  streamChat: async ({
    sessionId,
    message,
    history = [],
    debug = false,
    onToken,
    onStatus,
    signal,
  }) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };

    const res = await fetch(`${CHATBOT_BASE_URL}/chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session_id: sessionId,
        history,
        new_message: message,
        debug,
      }),
      signal,
    });

    if (!res.ok) {
      throw new Error(`Chat request failed (${res.status})`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let complete = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, remainder } = parseSSEChunk(buffer);
      buffer = remainder;

      for (const { event, data } of events) {
        if (event === 'token' && data?.text) onToken?.(data.text);
        else if (event === 'status') {
          onStatus?.(data);
          // Yield so React can paint this stage before later events in the same chunk.
          await new Promise((r) => setTimeout(r, 0));
        } else if (event === 'complete') complete = data;
        else if (event === 'error') {
          throw new Error(data?.message || 'Chat stream error');
        }
      }
    }

    if (!complete) {
      throw new Error('Chat stream ended without a complete event');
    }
    return complete;
  },
};

// Chat Session API — persists conversation history per user account.
// All requests carry _skipLogout so a backend 401 never boots the user out;
// these are best-effort background syncs.
const _cs = { _skipLogout: true };
export const chatSessionAPI = {
  /** Fetch all sessions for the current user */
  getAll: () => apiClient.get('/chat-sessions/', { _skipLogout: true }),

  /** Create a new session */
  create: (data) => apiClient.post('/chat-sessions/', data, _cs),

  /** Update an existing session (messages, preview) */
  update: (id, data) => apiClient.patch(`/chat-sessions/${id}/`, data, _cs),

  /** Delete a single session */
  delete: (id) => apiClient.delete(`/chat-sessions/${id}/`, _cs),

  /** Delete all sessions for the current user */
  clearAll: () => apiClient.delete('/chat-sessions/clear_all/', _cs),

  /** Upsert a batch of sessions from localStorage (migration on first login) */
  bulkSync: (sessions) => apiClient.post('/chat-sessions/bulk_sync/', sessions, _cs),
};
export default apiClient;
